import { Alert, Button, cardClass, fieldClass, selectClass } from '@ledgerloom/ui';
import { useState } from 'react';
import { propose } from '../api';
import { useApi, usePageTitle } from '../hooks';
import { hasFeature, isAdmin, useSession } from '../session';
import { ErrorBanner, Loading, outcomeMessage, outcomeTone, useToast } from '../components/ui';
import { Field } from '../components/Field';
import { keyFrom } from '../contactSettings';
import { SettingsLayout } from './Admin';
import type { SlaPolicy, TicketMove, TicketQueue, TicketSettingsData, TicketState, TicketType, TicketView } from './Tickets';

// Settings, Tickets: the types with their states and moves, the queues, the
// service levels, and the views. Everyone sees what is configured; an admin
// changes it, each a change set another admin signs. Options show only after
// Edit. Patterns: navigation/sidebar (the Settings menu), data-display/list-view,
// forms/text-field, forms/selection-input.

const REASON = 'Ticket settings from Settings';
const CLASS_WORD: Record<TicketState['class'], string> = { open: 'Open', pending: 'Waiting', resolved: 'Resolved', closed: 'Closed' };
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
const PRIORITY_WORD: Record<string, string> = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };
const WHO_WORD: Record<string, string> = { person: 'a person', agent: 'an agent', system: 'mail from the requester' };
const hours = (m: number) => (m % 60 ? `${m} minutes` : `${m / 60} hour${m === 60 ? '' : 's'}`);

function useProposal(onSaved: () => unknown) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const send = async (schema: string, payload: Record<string, unknown>, saved: string) => {
    setBusy(true); setError(null);
    try {
      const out = await propose(schema, { reason: REASON, ...payload });
      toast(outcomeMessage(out, saved), outcomeTone(out));
      if (out.state !== 'rejected') await onSaved();
      return out.state !== 'rejected';
    } catch (e) { setError(e); return false; } finally { setBusy(false); }
  };
  return { busy, error, send };
}

function TypeCard({ type, taken, queues, policies, admin, onSaved }: { type: TicketType | null; taken: string[]; queues: TicketQueue[]; policies: SlaPolicy[]; admin: boolean; onSaved: () => unknown }) {
  const [editing, setEditing] = useState(!type);
  const [name, setName] = useState(type?.name ?? '');
  const [states, setStates] = useState<TicketState[]>(type?.states ?? [{ id: 'open', label: 'Open', class: 'open' }, { id: 'done', label: 'Done', class: 'resolved' }]);
  const [moves, setMoves] = useState<TicketMove[]>(type?.transitions ?? [{ from: 'open', to: 'done', who: ['person'] }]);
  const [queue, setQueue] = useState(type?.default_queue ?? queues[0]?.key ?? '');
  const [policy, setPolicy] = useState(type?.sla_policy ?? '');
  const { busy, error, send } = useProposal(onSaved);
  const label = (id: string) => states.find((s) => s.id === id)?.label ?? id;
  if (!editing && type) {
    return (
      <li className="stack" style={{ gap: 6 }} aria-label={type.name}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <strong>{type.name}</strong>
          {admin && <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit</Button>}
        </div>
        <ol className="row small" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 6, flexWrap: 'wrap' }} aria-label={`States of ${type.name}`}>
          {type.states.map((s, i) => <li key={s.id}>{i > 0 && '→ '}{s.label} <span className="muted">({CLASS_WORD[s.class]})</span></li>)}
        </ol>
        <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }} aria-label={`Moves of ${type.name}`}>
          {type.transitions.map((m) => <li key={`${m.from}-${m.to}`}>{label(m.from)} to {label(m.to)}, by {m.who.map((w) => WHO_WORD[w]).join(' or ')}</li>)}
        </ul>
        <p className="small muted" style={{ margin: 0 }}>Queue: {queues.find((q) => q.key === type.default_queue)?.name ?? 'None'} · Service level: {policies.find((p) => p.key === type.sla_policy)?.name ?? 'None'}</p>
      </li>
    );
  }
  const save = () => send('config.ticket_type', {
    key: type?.key ?? keyFrom(name, taken), name: name.trim(), states, transitions: moves, fields: type?.fields ?? [], default_queue: queue, ...(policy ? { sla_policy: policy } : {}),
  }, 'Ticket type saved').then((ok) => { if (ok) setEditing(false); });
  return (
    <li className="stack" aria-label={type ? `Edit ${type.name}` : 'New ticket type'}>
      <ErrorBanner error={error} />
      <Field label="Name"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <div className="stack" style={{ gap: 6 }} role="group" aria-label="States">
        <strong className="small">States, the first is where a ticket starts</strong>
        {states.map((s, i) => (
          <div key={i} className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label={`State ${i + 1}`}><input className={fieldClass} value={s.label} onChange={(e) => setStates(states.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} /></Field>
            <Field label={`Kind of state ${i + 1}`}><select className={selectClass} value={s.class} onChange={(e) => setStates(states.map((x, j) => (j === i ? { ...x, class: e.target.value as TicketState['class'] } : x)))}>{Object.entries(CLASS_WORD).map(([k, w]) => <option key={k} value={k}>{w}</option>)}</select></Field>
            <Button type="button" variant="ghost" size="sm" aria-label={`Take off state ${i + 1}`} onClick={() => { setStates(states.filter((_, j) => j !== i)); setMoves(moves.filter((m) => m.from !== s.id && m.to !== s.id)); }}>Take off</Button>
          </div>
        ))}
        <div><Button type="button" variant="ghost" size="sm" onClick={() => setStates([...states, { id: keyFrom(`state ${states.length + 1}`, states.map((o) => o.id)), label: `State ${states.length + 1}`, class: 'open' }])}>Add a state</Button></div>
      </div>
      <div className="stack" style={{ gap: 6 }} role="group" aria-label="Moves">
        <strong className="small">Moves between states</strong>
        {moves.map((m, i) => (
          <div key={i} className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <Field label={`Move ${i + 1} from`}><select className={selectClass} value={m.from} onChange={(e) => setMoves(moves.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}>{states.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></Field>
            <Field label={`Move ${i + 1} to`}><select className={selectClass} value={m.to} onChange={(e) => setMoves(moves.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}>{states.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}</select></Field>
            {Object.entries(WHO_WORD).map(([w, word]) => (
              <label key={w} className="row small" style={{ gap: 4 }}><input type="checkbox" checked={m.who.includes(w)} onChange={(e) => setMoves(moves.map((x, j) => (j === i ? { ...x, who: e.target.checked ? [...x.who, w] : x.who.filter((o) => o !== w) } : x)))} /> {word}</label>
            ))}
            <Button type="button" variant="ghost" size="sm" aria-label={`Take off move ${i + 1}`} onClick={() => setMoves(moves.filter((_, j) => j !== i))}>Take off</Button>
          </div>
        ))}
        <div><Button type="button" variant="ghost" size="sm" onClick={() => setMoves([...moves, { from: states[0]!.id, to: states[states.length - 1]!.id, who: ['person'] }])} disabled={!states.length}>Add a move</Button></div>
      </div>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
        <Field label="Queue"><select className={selectClass} value={queue} onChange={(e) => setQueue(e.target.value)}>{queues.map((q) => <option key={q.key} value={q.key}>{q.name}</option>)}</select></Field>
        <Field label="Service level"><select className={selectClass} value={policy} onChange={(e) => setPolicy(e.target.value)}><option value="">None</option>{policies.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}</select></Field>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <Button type="button" variant="secondary" size="sm" disabled={busy || !name.trim() || !states.length || !queue || moves.some((m) => !m.who.length)} onClick={() => void save()}>Save</Button>
        {type && <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>}
      </div>
    </li>
  );
}

function QueueCard({ queue, mailboxes, admin, onSaved }: { queue: TicketQueue; mailboxes: Array<{ id: string; kind: string; name: string }> | null; admin: boolean; onSaved: () => unknown }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(queue.name);
  const [mailbox, setMailbox] = useState(queue.mailbox_id ?? '');
  const { busy, error, send } = useProposal(onSaved);
  if (!editing) {
    return (
      <li className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }} aria-label={queue.name}>
        <span><strong>{queue.name}</strong> <span className="small muted">{queue.mailbox_name ? `Mail to ${queue.mailbox_name} opens tickets here` : 'No mailbox: tickets here come from people and agents'}</span></span>
        {admin && <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit</Button>}
      </li>
    );
  }
  return (
    <li>
      <form className="stack" aria-label={`Edit ${queue.name}`} onSubmit={(e) => { e.preventDefault(); void send('config.ticket_queue', { key: queue.key, name: name.trim(), mailbox_id: mailbox || null, members: queue.members, default_assignee: queue.default_assignee }, 'Queue saved').then((ok) => { if (ok) setEditing(false); }); }}>
        <ErrorBanner error={error} />
        <Field label="Name"><input className={fieldClass} value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Team mailbox" hint="Mail to it opens tickets here instead of waiting in Mail as a lead.">
          <select className={selectClass} value={mailbox} onChange={(e) => setMailbox(e.target.value)}><option value="">None</option>{(mailboxes ?? []).filter((m) => m.kind === 'team').map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
        </Field>
        <div className="row" style={{ gap: 8 }}>
          <Button type="submit" variant="secondary" size="sm" disabled={busy || !name.trim()}>Save</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      </form>
    </li>
  );
}

function PolicyCard({ policy, admin, onSaved }: { policy: SlaPolicy; admin: boolean; onSaved: () => unknown }) {
  const [editing, setEditing] = useState(false);
  const [first, setFirst] = useState(policy.first_response_minutes);
  const [resolution, setResolution] = useState(policy.resolution_minutes);
  const [calendar, setCalendar] = useState(policy.calendar);
  const { busy, error, send } = useProposal(onSaved);
  const word = calendar === '24x7' ? 'around the clock' : 'in business hours, 09:00 to 17:00, Monday to Friday';
  if (!editing) {
    return (
      <li className="stack" style={{ gap: 4 }} aria-label={policy.name}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
          <strong>{policy.name}</strong>
          {admin && <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)}>Edit</Button>}
        </div>
        <p className="small muted" style={{ margin: 0 }}>Counted {word}. The clock stops {policy.pauses_in.includes('pending') ? 'while a ticket waits on the requester, and ' : ''}once it is resolved.</p>
        <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
          {PRIORITIES.map((p) => <li key={p}>{PRIORITY_WORD[p]}: first response in {hours(policy.first_response_minutes[p]!)}, resolved in {hours(policy.resolution_minutes[p]!)}</li>)}
        </ul>
      </li>
    );
  }
  return (
    <li>
      <form className="stack" aria-label={`Edit ${policy.name}`} onSubmit={(e) => { e.preventDefault(); void send('config.sla_policy', { key: policy.key, name: policy.name, first_response_minutes: first, resolution_minutes: resolution, calendar, pauses_in: policy.pauses_in }, 'Service level saved').then((ok) => { if (ok) setEditing(false); }); }}>
        <ErrorBanner error={error} />
        <Field label="Counted"><select className={selectClass} value={calendar} onChange={(e) => setCalendar(e.target.value as SlaPolicy['calendar'])}><option value="business_hours">In business hours</option><option value="24x7">Around the clock</option></select></Field>
        {PRIORITIES.map((p) => (
          <div key={p} className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <Field label={`${PRIORITY_WORD[p]}: first response, in minutes`}><input className={fieldClass} type="number" min={1} step={1} value={first[p]} onChange={(e) => setFirst({ ...first, [p]: Number(e.target.value) })} /></Field>
            <Field label={`${PRIORITY_WORD[p]}: resolved, in minutes`}><input className={fieldClass} type="number" min={1} step={1} value={resolution[p]} onChange={(e) => setResolution({ ...resolution, [p]: Number(e.target.value) })} /></Field>
          </div>
        ))}
        <div className="row" style={{ gap: 8 }}>
          <Button type="submit" variant="secondary" size="sm" disabled={busy}>Save</Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      </form>
    </li>
  );
}

function ViewRow({ view, onSaved }: { view: TicketView; onSaved: () => unknown }) {
  const { busy, error, send } = useProposal(onSaved);
  return (
    <li className="row" style={{ justifyContent: 'space-between', gap: 8 }} aria-label={view.name}>
      <span><strong>{view.name}</strong> <span className="small muted">{view.shared ? 'Shared with everyone' : 'Yours'}</span></span>
      <ErrorBanner error={error} />
      <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void send('config.ticket_view', { view_id: view.id, name: view.name, filter: view.filter, columns: view.columns, board: view.board, shared: view.shared, archived: true }, 'View taken off')}>Take off</Button>
    </li>
  );
}

const listStyle = { listStyle: 'none', padding: 0, margin: 0 } as const;

export function TicketSettings() {
  usePageTitle('Ticket settings');
  const { me } = useSession();
  const admin = isAdmin(me);
  const settings = useApi<TicketSettingsData>('/v1/tickets/settings');
  const mailboxes = useApi<Array<{ id: string; kind: string; name: string }>>(admin && hasFeature(me, 'mail') ? '/v1/mailboxes' : null);
  const [adding, setAdding] = useState(false);
  const s = settings.data;
  const saved = () => settings.reload();
  return (
    <SettingsLayout title="Tickets" lead="Ticket types and the moves between their states, queues and the mailboxes they read, service levels, and saved views.">
      {!admin && <Alert role="note" tone="info">Only an admin changes these. Ask one if something should change.</Alert>}
      {admin && <Alert role="note" tone="info">Each change is proposed as a change set. Another admin signs it before it applies.</Alert>}
      <ErrorBanner error={settings.error ?? mailboxes.error} />
      {!s ? <Loading label="Loading ticket settings" /> : <>
        <section className={`${cardClass} card-pad stack`} aria-label="Ticket types">
          <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
            <h2>Ticket types</h2>
            {admin && !adding && <Button type="button" variant="secondary" size="sm" onClick={() => setAdding(true)}>New ticket type</Button>}
          </div>
          <ul className="stack" style={listStyle}>
            {s.types.map((t) => <TypeCard key={`${t.id}-${t.version}`} type={t} taken={[]} queues={s.queues} policies={s.sla_policies} admin={admin} onSaved={saved} />)}
            {adding && <TypeCard type={null} taken={s.types.map((t) => t.key)} queues={s.queues} policies={s.sla_policies} admin={admin} onSaved={() => { setAdding(false); return saved(); }} />}
          </ul>
        </section>
        <section className={`${cardClass} card-pad stack`} aria-label="Queues">
          <h2>Queues</h2>
          <ul className="stack" style={listStyle}>{s.queues.map((q) => <QueueCard key={`${q.id}-${q.version}`} queue={q} mailboxes={mailboxes.data} admin={admin} onSaved={saved} />)}</ul>
        </section>
        <section className={`${cardClass} card-pad stack`} aria-label="Service levels">
          <h2>Service levels</h2>
          <ul className="stack" style={listStyle}>{s.sla_policies.map((p) => <PolicyCard key={`${p.id}-${p.version}`} policy={p} admin={admin} onSaved={saved} />)}</ul>
        </section>
        {s.views.length > 0 && (
          <section className={`${cardClass} card-pad stack`} aria-label="Saved views">
            <h2>Saved views</h2>
            <ul className="stack" style={listStyle}>{s.views.map((v) => <ViewRow key={v.id} view={v} onSaved={saved} />)}</ul>
          </section>
        )}
      </>}
    </SettingsLayout>
  );
}
